import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { View } from 'react-native'
import { connect, useDispatch, useSelector } from 'react-redux'

import { cloneDeep, isEmpty } from 'lodash-es'
import { AnyAction, bindActionCreators, Dispatch } from 'redux'
import styled, { useTheme } from 'styled-components/native'

import { getProgramDisplayLabel } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { isDAProvider, isICASCounsellor } from '@lyrahealth-inc/shared-app-logic/src/features/providers/utils'
import { ProviderAvailability } from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { LTProgramName } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { ProviderAdminProviderInfo } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { Capacity, ProviderExpeditedBookingConfig } from '@lyrahealth-inc/shared-app-logic/src/models/user/User'
import { SelectField } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/SelectField'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { SecondaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/secondaryButton/SecondaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import InPersonToggle from './InPersonToggle'
import styles from './providerAvailabilityModal.module.scss'
import { mixpanelEvents } from '../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../mixpanel/mixpanelTracking'
import { ExpeditedBookingToggle } from '../../common/components/forms/expeditedBooking/ExpeditedBookingToggle'
import {
  INCREASED_PROVIDER_MAX_CAPACITY,
  LT_PROVIDER_MAX_CAPACITY,
  programConfig,
} from '../../common/constants/appConstants'
import { getProgramIcon } from '../../common/constants/programIconMap'
import { actionAlertHandler, actionStyles } from '../../common/constants/reduxConstants'
import { shouldShowExpeditedBooking } from '../../common/utils/expeditedBooking'
import { getAuthConfig, getAuthExpeditedBookingConfig, getAuthUser } from '../../data/auth/authSelectors'
import {
  updateExpeditedBookingStatus,
  updateInPersonDesireStatus,
  updateProviderCapacity,
  updateProviderGracePeriod,
  updateSelectedProviderCapacity,
} from '../../providers/data/providersDataActions'
import OutOfOfficeContainer from '../../providers/outOfOffice/OutOfOfficeContainer'
import { setToastContent } from '../data/ltToastAutoActions'

const TabContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['24px'],
  borderBottomWidth: '1px',
  borderBottomColor: theme.colors.borderDefault,
}))

const CapacityContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.spacing['24px'],
}))

const ModalNavigation = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  display: 'flex',
  justifyContent: 'end',
  flexDirection: 'row',
  gap: theme.spacing['24px'],
}))

const DecorativeIconContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  position: 'absolute',
  zIndex: 1,
  top: theme.spacing['8px'],
  left: theme.spacing['8px'],
}))

const LinkActiveUnderline = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  width: '100%',
  height: '3px',
  backgroundColor: theme.colors.backgroundActive,
  position: 'absolute',
  borderTopLeftRadius: theme.spacing['16px'],
  borderTopRightRadius: theme.spacing['16px'],
  left: 0,
  right: 0,
  bottom: 0,
  marginLeft: 'auto',
  marginRight: 'auto',
}))

const Subheader = styled(Title)<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['8px'],
}))

const AvailabilityModalOpened = styled.View({
  marginTop: '36px',
})

const ProviderAvailabilityModal: React.FC<ProviderAvailabilityModalProps> = ({
  providerId,
  selectedProvider,
  closeModal,
  currentCapacity,
  providerAvailabilities,
  triggerMismatchModal,
  setMismatchModalCount,
  // eslint-disable-next-line @typescript-eslint/no-shadow
  actions: { updateProviderCapacity, updateExpeditedBookingStatus, updateSelectedProviderCapacity, setToastContent },
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const { colors } = useTheme()
  const providerExpeditedBookingConfig: ProviderExpeditedBookingConfig | undefined =
    useSelector(getAuthExpeditedBookingConfig)
  const config = useSelector(getAuthConfig)
  const authUser = useSelector(getAuthUser)
  // DA and ICAS (international) providers share the same increased default max capacity. Channel
  // comes from the program taxonomy partner (DirectAccess or Icas): the logged-in user's when
  // editing self, or the selected provider's when an admin edits someone else.
  const programTaxonomy = isEmpty(selectedProvider)
    ? (authUser?.program_taxonomies ?? [])
    : ((selectedProvider as ProviderAdminProviderInfo).program_taxonomies ?? [])
  const hasIncreasedMaxCapacity = isDAProvider({ programTaxonomy }) || isICASCounsellor({ programTaxonomy })
  const defaultMaxCapacity = hasIncreasedMaxCapacity ? INCREASED_PROVIDER_MAX_CAPACITY : LT_PROVIDER_MAX_CAPACITY
  const tabs = {
    AVAILABILITY: 'New client capacity',
    AWAY_MESSAGE: 'Out of office message',
  }

  const [loadingCapacity, setLoadingCapacity] = useState(false)
  const [expeditedBookingErrors, setExpeditedBookingErrors] = useState<Record<string, boolean>>({})
  const [expeditedBookingPayload, setExpeditedBookingPayload] = useState<ProviderExpeditedBookingConfig>({})
  const [showInPersonSuccess, setShowInPersonSuccess] = useState(false)
  const [selectedCapacities, setSelectedCapacities] = useState(currentCapacity)
  const [selectedTab, setSelectedTab] = useState(tabs.AVAILABILITY)
  const [showTabs, setShowTabs] = useState(!!config?.showOOOMessageTab)

  useEffect(() => {
    if (providerExpeditedBookingConfig) {
      setExpeditedBookingPayload(providerExpeditedBookingConfig)
    }
  }, [providerExpeditedBookingConfig])
  const onUpdateAvailability = () => {
    track({
      event: 'UPDATE_CAPACITY',
      details: `New capacity: ${{ ...selectedCapacities }}`,
    })
    const newTotalCapacity = Object.values(selectedCapacities).reduce<number>(
      (sum, v) => sum + (Number.isInteger(v) ? (v as number) : 0),
      0,
    )
    const capacityChanged = Object.keys({ ...currentCapacity, ...selectedCapacities }).some(
      (key) => (currentCapacity[key as keyof Capacity] ?? 0) !== (selectedCapacities[key as keyof Capacity] ?? 0),
    )
    setLoadingCapacity(true)
    const updateCapacity = isEmpty(selectedProvider) ? updateProviderCapacity : updateSelectedProviderCapacity
    for (const [key, value] of Object.entries(expeditedBookingPayload)) {
      const valueDict = value as Dict
      if (
        valueDict &&
        valueDict?.enabled &&
        (!Object.prototype.hasOwnProperty.call(valueDict, 'grace_period') ||
          !Number.isInteger(valueDict.grace_period) ||
          valueDict.grace_period === 0)
      ) {
        setExpeditedBookingErrors({ [key]: true })
        setLoadingCapacity(false)
        return
      }
    }

    const promises = []
    let totalCombinedCapacities = 0

    // if user submits dropdown with the option of 'Select', an empty string is passed, but we convert it to a 0
    for (const [key, value] of Object.entries(selectedCapacities)) {
      totalCombinedCapacities += selectedCapacities[key as keyof Capacity] as number
      if (!Number.isInteger(value)) {
        selectedCapacities[key as keyof Capacity] = 0
      }
    }

    // always update capacities even if it is unchanged, because we use timestamp of last update for DA providers
    promises.push(
      updateCapacity({
        id: providerId,
        data: { capacity: { ...selectedCapacities } },
      }),
    )

    let expeditedBookingValuesChanged = false
    // if we submit a program with disabled Expedited, we want the grace_period to be 0
    if (expeditedBookingPayload && Object.keys(expeditedBookingPayload).length > 0) {
      const cleanedPayload = cloneDeep(expeditedBookingPayload)
      for (const key of Object.keys(expeditedBookingPayload)) {
        const programValues = expeditedBookingPayload[key]
        const savedProgramValues = providerExpeditedBookingConfig?.[key]
        if (!programValues.enabled && Number.isInteger(programValues.grace_period)) {
          cleanedPayload[key].grace_period = 0
        }
        if (
          !savedProgramValues ||
          savedProgramValues.enabled !== programValues.enabled ||
          savedProgramValues.grace_period !== programValues.grace_period
        ) {
          expeditedBookingValuesChanged = true
        }
      }

      if (expeditedBookingValuesChanged) {
        track({ event: 'UPDATING_EXPEDITED_BOOKING' })
        promises.push(
          updateExpeditedBookingStatus({ id: providerId, data: { value: cleanedPayload, value_type: 'json' } }),
        )
      }
    }

    Promise.all(promises)
      .then(() => {
        track({
          event: mixpanelEvents.CAPACITY_UPDATED,
          properties: { total_capacity: newTotalCapacity, changed: capacityChanged },
        })
      })
      .catch((error: string) => {
        actionAlertHandler({
          actionStyle: actionStyles.ERROR,
          message: error,
          dispatch,
          action: undefined,
          expires: undefined,
        })
      })
      .finally(() => {
        closeModal()
        setToastContent({
          text: 'Capacity set!',
          id: 'Capacity-Saved-Toast-Success',
          toastType: 'success',
        })
        if (
          triggerMismatchModal &&
          Array.isArray(providerAvailabilities) &&
          totalCombinedCapacities > providerAvailabilities.length
        ) {
          setMismatchModalCount(totalCombinedCapacities - providerAvailabilities.length)
          triggerMismatchModal()
        }
        setLoadingCapacity(false)
        setShowInPersonSuccess(false)
      })
  }

  const renderCapacityInputs = (currentCapacity: Capacity) => {
    return Object.entries(currentCapacity).map(([programName]) => {
      const ProgramIcon = getProgramIcon(programName)
      const programSameDayAvailability =
        (expeditedBookingPayload && expeditedBookingPayload[programName]?.enabled) || false
      const maxCapacity = programConfig[programName as LTProgramName]?.maxCapacity ?? defaultMaxCapacity
      const showExpeditedBooking = shouldShowExpeditedBooking({
        programName,
        programTaxonomies: programTaxonomy,
        providerRoles: authUser?.roles ?? [],
        providerOnsite: authUser?.onsite,
        isSelfService: providerId === authUser?.id,
        roleAllowsExpeditedBooking: config?.sameDayBooking,
      })
      return (
        <React.Fragment key={programName}>
          <Subheader
            size={TitleSize.SMALL}
            text={getProgramDisplayLabel(intl, programName)}
            color={colors.textSecondary}
          />
          <SelectField
            onChange={(value) => {
              setSelectedCapacities({
                ...selectedCapacities,
                [programName]: value,
              })
            }}
            value={selectedCapacities[programName as keyof Capacity] as number}
            name={`ProviderAvailabilityModal-capacity-${programName}`}
            options={[...Array(maxCapacity + 1).keys()].map((i) => {
              return {
                label: i === 0 ? '0 - Not available for new Lyra clients' : `${i}`,
                value: i,
              }
            })}
            decorativeIcon={
              ProgramIcon && (
                <DecorativeIconContainer>
                  <ProgramIcon width={30} height={30} />
                </DecorativeIconContainer>
              )
            }
            baseInputStyle={{
              marginBottom: 8,
            }}
          />

          {showExpeditedBooking && (
            <ExpeditedBookingToggle
              gracePeriod={expeditedBookingPayload && expeditedBookingPayload[programName]?.grace_period}
              onChangeToggle={(programName, status) => {
                track({ event: 'TOGGLED_EXPEDITED_BOOKING' })
                setExpeditedBookingPayload((prevState) => {
                  const updated = { ...prevState }
                  updated[programName] = { ...updated[programName], enabled: status }
                  return updated
                })
              }}
              onChangeGracePeriod={(programName, buffer) => {
                track({ event: 'SELECTED_EXPEDITED_BOOKING_GRACE_PERIOD' })
                setExpeditedBookingErrors({})
                setExpeditedBookingPayload((prevState) => {
                  const updated = { ...prevState }
                  updated[programName] = { ...updated[programName], grace_period: buffer }
                  return updated
                })
              }}
              programName={programName}
              status={programSameDayAvailability}
              displayError={expeditedBookingErrors[programName]}
            />
          )}
        </React.Fragment>
      )
    })
  }

  return (
    <AvailabilityModalOpened testID={tID('AvailabilityModal-opened')}>
      {(showTabs || !config?.bcPlatform) && <h3 style={{ margin: '0px 0 30px 0' }}>Update capacity</h3>}
      {showTabs && (
        <TabContainer>
          <View>
            <Link
              color={selectedTab === tabs.AVAILABILITY ? colors.linkDefault : colors.textSecondary}
              testID={tID('ProviderAvailability-availability')}
              onPress={() => setSelectedTab(tabs.AVAILABILITY)}
              style={{
                padding: 6,
                paddingBottom: 8,
                fontSize: 12,
                textTransform: 'uppercase',
              }}
              text={tabs.AVAILABILITY}
            />
            {selectedTab === tabs.AVAILABILITY && <LinkActiveUnderline testID={tID(`Header-active`)} />}
          </View>
          <View>
            <Link
              color={selectedTab === tabs.AWAY_MESSAGE ? colors.linkDefault : colors.textSecondary}
              testID={tID('ProviderAvailability-outOfOffice')}
              onPress={() => {
                track({ event: 'SHOW_OOO_TAB' })
                setSelectedTab(tabs.AWAY_MESSAGE)
              }}
              style={{
                padding: 6,
                paddingBottom: 8,
                fontSize: 12,
                textTransform: 'uppercase',
              }}
              text={tabs.AWAY_MESSAGE}
            />
            {selectedTab === tabs.AWAY_MESSAGE && <LinkActiveUnderline testID={tID(`Header-active`)} />}
          </View>
        </TabContainer>
      )}

      {selectedTab === tabs.AVAILABILITY ? (
        <>
          <CapacityContainer>{renderCapacityInputs(currentCapacity)}</CapacityContainer>
          <ModalNavigation>
            <SecondaryButton
              fullWidth={true}
              text={'Cancel'}
              onPress={() => {
                closeModal()
                setShowInPersonSuccess(false)
              }}
              testID={tID('ProviderAvailabilityModal-cancel')}
            />
            <PrimaryButton
              fullWidth={true}
              text={'Save'}
              onPress={onUpdateAvailability}
              testID={tID('ProviderAvailabilityModal-update')}
              loading={loadingCapacity}
            />
          </ModalNavigation>
          <InPersonToggle
            showInPersonSuccess={showInPersonSuccess}
            onPressAfterUpdate={() => setShowInPersonSuccess(true)}
            providerId={providerId}
            testID={tID('ProviderAvailabilityModal-in-person-button')}
            customStyles={styles}
          />
        </>
      ) : (
        <OutOfOfficeContainer closeModal={closeModal} setShowTabs={setShowTabs} />
      )}
    </AvailabilityModalOpened>
  )
}

type ProviderAvailabilityModalProps = {
  currentCapacity: Capacity
  providerId: string
  selectedProvider: ProviderAdminProviderInfo | object
  closeModal: () => void
  providerAvailabilities: ProviderAvailability[]
  setMismatchModalCount: (count: number) => void
  triggerMismatchModal?: () => void
  actions: {
    updateProviderCapacity: ({ id, data }: { id: string; data: { capacity: Capacity } }) => Promise<any>
    updateSelectedProviderCapacity: ({ id, data }: { id: string; data: { capacity: Capacity } }) => Promise<any>
    updateExpeditedBookingStatus: ({
      id,
      data,
    }: {
      id: string
      data: { value: ProviderExpeditedBookingConfig; value_type: string }
    }) => Promise<any>
    updateInPersonDesireStatus: ({ id, data }: { id: string; data: { in_person_preference: boolean } }) => Promise<any>
    setToastContent: ({ text, id, toastType }: { text: string; id: string; toastType: string }) => void
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => {
  return {
    actions: bindActionCreators(
      {
        updateProviderCapacity: updateProviderCapacity as unknown as any,
        updateProviderGracePeriod: updateProviderGracePeriod as unknown as any,
        updateExpeditedBookingStatus: updateExpeditedBookingStatus as unknown as any,
        updateSelectedProviderCapacity: updateSelectedProviderCapacity as unknown as any,
        updateInPersonDesireStatus: updateInPersonDesireStatus as unknown as any,
        setToastContent: setToastContent as unknown as any,
      },
      dispatch,
    ),
  }
}

export default connect(null, mapDispatchToProps)(ProviderAvailabilityModal)
